import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../Button';
import { Icons } from '../Icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../DropdownMenu';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '../Tooltip';

/**
 * ToolButtonList Component
 * Root component that wraps the default and dropdown sections
 * -----------------------------------------------
 */
interface ToolButtonListProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const ToolButtonList = React.forwardRef<HTMLDivElement, ToolButtonListProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('relative flex items-center', className)} // 保持 relative 定位
        {...props}
      >
        {children}
      </div>
    );
  }
);
ToolButtonList.displayName = 'ToolButtonList';

/**
 * ToolButtonListDefault Component
 * Container for the default/primary tool button
 * -----------------------------------------------
 */
interface ToolButtonListDefaultProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  tooltip?: string;
  disabledText?: string;
  disabled?: boolean;
}

const ToolButtonListDefault = React.forwardRef<HTMLDivElement, ToolButtonListDefaultProps>(
  ({ className, children, tooltip, disabledText, disabled, ...props }, ref) => {
    const hasTooltip = tooltip || disabledText;

    const defaultContent = (
      <div
        ref={ref}
        className={cn('relative flex items-center', className)}
        {...props}
      >
        {children}
      </div>
    );

    if (!hasTooltip) {
      return defaultContent;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{defaultContent}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {tooltip && <div>{tooltip}</div>}
          {disabledText && disabled && <div className="text-muted-foreground">{disabledText}</div>}
        </TooltipContent>
      </Tooltip>
    );
  }
);
ToolButtonListDefault.displayName = 'ToolButtonListDefault';

/**
 * ToolButtonListDropDown Component
 * Container for the dropdown section with trigger and content
 * -----------------------------------------------
 */
interface ToolButtonListDropDownProps {
  children: React.ReactNode;
  className?: string;
  toolbarRef: React.RefObject<HTMLDivElement>; // 接收工具栏ref
}

const ToolButtonListDropDown = React.forwardRef<HTMLDivElement, ToolButtonListDropDownProps>(
  ({ children, className, toolbarRef, ...props }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const lastScrollTime = useRef(0);

    // 处理工具栏滚动事件
    useEffect(() => {
      const handleScroll = () => {
        const now = Date.now();
        lastScrollTime.current = now;

        // 如果菜单是打开的，关闭它
        if (isOpen) {
          setIsOpen(false);
        }

        setIsScrolling(true);

        // 延迟重置滚动状态
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          // 确保没有新的滚动事件发生
          if (Date.now() - lastScrollTime.current > 300) {
            setIsScrolling(false);
          }
        }, 300);
      };

      const toolbarEl = toolbarRef.current;
      if (toolbarEl) {
        toolbarEl.addEventListener('scroll', handleScroll);
      }

      return () => {
        if (toolbarEl) {
          toolbarEl.removeEventListener('scroll', handleScroll);
        }
        clearTimeout(window.scrollTimeout);
      };
    }, [toolbarRef, isOpen]);

    // 处理下拉菜单内部滚动
    const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
      // 阻止事件冒泡到工具栏
      e.stopPropagation();
    };

    // 优化的切换下拉菜单状态
    const handleToggle = useCallback(() => {
      // 只有在滚动停止后才能切换菜单状态
      if (!isScrolling) {
        setIsOpen(prev => !prev);
      }
    }, [isScrolling]);

    // 点击外部关闭下拉菜单
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          contentRef.current &&
          !contentRef.current.contains(e.target as Node) &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // 特殊处理触发按钮的点击事件
    const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // 防止点击事件被工具栏捕获
      e.stopPropagation();

      // 只有在滚动停止后才能处理点击
      if (Date.now() - lastScrollTime.current > 300) {
        handleToggle();
      }
    };

    // 处理菜单项点击
    const handleItemClick = (e: React.MouseEvent) => {
      // 防止点击事件冒泡到工具栏
      e.stopPropagation();

      // 关闭下拉菜单
      setIsOpen(false);
    };

    return (
      <DropdownMenu
        {...props}
        open={isOpen}
        onOpenChange={() => {}} // 禁用默认行为，使用自定义处理
      >
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="icon"
            className={cn(
              'absolute bottom-0 right-[-7px]', // 定位到右下角
              'h-full w-6', // 小型按钮
              'border-0 hover:bg-transparent active:bg-transparent'
            )}
            onClick={handleTriggerClick}
          >
            <Icons.ByName
              name="chevron-down"
              className="text-primary h-4 w-4"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          ref={contentRef}
          side="bottom" // 菜单在触发按钮下方
          align="end" // 右对齐
          alignOffset={0} // 无偏移
          sideOffset={4} // 向下偏移以避免重叠
          className="min-w-[150px]" // 确保菜单宽度
          style={{ overflowY: 'auto', maxHeight: '200px' }} // 添加内部滚动
          onScroll={handleContentScroll} // 监听内部滚动
        >
          {/* 克隆子元素并添加点击处理 */}
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                onClick: (e: React.MouseEvent) => {
                  // 执行子元素自身的点击处理
                  if (typeof child.props.onClick === 'function') {
                    child.props.onClick(e);
                  }

                  // 关闭下拉菜单
                  handleItemClick(e);
                }
              });
            }
            return child;
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);
ToolButtonListDropDown.displayName = 'ToolButtonListDropDown';

/**
 * ToolButtonListItem Component
 * Individual item in the dropdown menu
 * -----------------------------------------------
 */
interface ToolButtonListItemProps extends React.ComponentProps<typeof DropdownMenuItem> {
  icon?: string;
  children?: React.ReactNode;
  className?: string;
  disabledText?: string;
  tooltip?: string;
  onClick?: (e: React.MouseEvent) => void; // 添加 onClick 支持
}

const ToolButtonListItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItem>,
  ToolButtonListItemProps
>(({ className, children, icon, disabledText, tooltip, disabled, onClick, ...props }, ref) => {
  const defaultTooltip = tooltip || (typeof children === 'string' ? children : undefined);

  const menuItem = (
    <DropdownMenuItem
      ref={ref}
      className={cn('flex items-center space-x-2', className)}
      disabled={disabled}
      onClick={onClick} // 传递 onClick 事件
      {...props}
    >
      {icon && (
        <Icons.ByName
          name={icon || 'MissingIcon'}
          className="h-6 w-6"
        />
      )}
      {children}
    </DropdownMenuItem>
  );

  if (!disabled) {
    return menuItem;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{menuItem}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {defaultTooltip && <div>{defaultTooltip}</div>}
        {disabledText && disabled && <div className="text-muted-foreground">{disabledText}</div>}
      </TooltipContent>
    </Tooltip>
  );
});
ToolButtonListItem.displayName = 'ToolButtonListItem';

/**
 * ToolButtonListDivider Component
 * Divider between items in the dropdown menu
 * -----------------------------------------------
 */
const ToolButtonListDivider = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('h-5 w-px self-center bg-white', className)}
    {...props}
  />
));
ToolButtonListDivider.displayName = 'ToolButtonListDivider';

export {
  ToolButtonList,
  ToolButtonListDefault,
  ToolButtonListDropDown,
  ToolButtonListItem,
  ToolButtonListDivider,
};
